"use client";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useAppSelector } from "../slice/hooks";
import StoreBox from "../store/template/default/store-box";
import CartModal from "../store/template/default/cart/cart-modal";
import styles from "../store/template/default/index.module.scss";
import { useToken } from "../../lib/context/auth-context";

import Skeleton from "react-loading-skeleton";
import { useSwitchingOrgs } from "../organisation/useSwitchingOrgs";
import { cartIcon } from "../../assets";

import { useHistoryManager } from "../history/useHistoryManager";
import {
  getAllCartsList,
} from "../slice/cart-slice";
import {
  getAccessPoint,
  getAccessPoints,
  getOrganisation,
} from "../slice/accessOrg-slice";
import { useMarketplaceCartCollections } from "./useMarketplaceCartCollections";

const cx = (...classNames) => classNames.filter(Boolean).join(" ");

const MarketplaceSkeleton = () => {
  return (
    <div>
      {Array.from({ length: 3 }).map((_, storeIdx) => (
        <div
          key={storeIdx}
          style={{
            padding: "10px",
            marginBottom: "24px",
            borderRadius: "12px",
            background: "#fff",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            <Skeleton width={48} height={48} borderRadius={8} />
            <Skeleton width={180} height={22} />
          </div>

          <div
            style={{
              borderRadius: "12px",
              padding: "16px",
              background: "#fafafa",
              display: "flex",
              flexDirection: "column",
              gap: "2rem",
            }}
          >
            {Array.from({ length: 3 }).map((_, itemIdx) => (
              <div
                key={itemIdx}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <Skeleton width={56} height={56} borderRadius={8} />

                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                  }}
                >
                  <Skeleton width="40%" height={14} />
                  <Skeleton width="70%" height={16} />
                </div>

                <Skeleton width={40} height={28} borderRadius={6} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const HostHeader = ({ host, onCheckout }) => {
  const [logoLoaded, setLogoLoaded] = useState(false);

  const hostImage = host?.image;
  const hostName = host?.name;

  const logoSize = 48;

  return (
    <div className={styles.marketplaceHostHeader}>
      <div className={styles.marketplaceHostInfo}>
        <div
          aria-hidden
          className={styles.marketplaceHostLogo}
          style={{
            width: logoSize,
            height: logoSize,
          }}
        >
          {hostImage ? (
            <>
              {!logoLoaded && <Skeleton width={logoSize} height={logoSize} />}
              <img
                src={hostImage}
                alt={hostName ? `${hostName} logo` : "Organisation logo"}
                onLoad={() => setLogoLoaded(true)}
                onError={() => setLogoLoaded(true)}
                style={{
                  width: logoSize,
                  height: logoSize,
                  display: logoLoaded ? "block" : "none",
                }}
              />
            </>
          ) : null}
        </div>

        <div className={styles.marketplaceHostText}>
          {hostName ? <span>{hostName}</span> : null}
        </div>
      </div>

      {onCheckout ? (
        <button
          className={cx(styles.checkoutButton, styles.marketplaceHostCheckout)}
          onClick={onCheckout}
        >
          Checkout from {hostName || "Store"}
        </button>
      ) : null}
    </div>
  );
};

const MarketplaceStoreDivider = ({ hostName }) => {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        margin: "40px 0",
        marginTop: "0",
        color: "#777",
        fontWeight: 500,
        fontSize: "1.2rem",
      }}
    >
      <div style={{ flex: 1, borderTop: "2px solid #c3c3c3" }} />
      <span
        style={{
          padding: "0 16px",
          whiteSpace: "nowrap",
          fontSize: "1.6rem",
          color: "#333",
          fontWeight: 600,
        }}
      >
        Store: {hostName || "New Store"}
      </span>
      <div style={{ flex: 1, borderTop: "2px solid #c3c3c3" }} />
    </div>
  );
};

const MarketplaceCartPage = (props) => {
  const {
    closeModal,
    showProduct,
    activateBackButton,
    storePath,
    modal,
    showProgress,
    setShowProgress,
    navLocation,
    urlData,
  } = props;

  const [isPageMode, setIsPageMode] = useState(modal ? false : true);
  const [publicUser, setPublicUser] = useState(null);

  const organisation = useAppSelector(getOrganisation);
  const accessPoint = useAppSelector(getAccessPoint);
  const accessPoints = useAppSelector(getAccessPoints);
  const cartsFromSlice = useAppSelector(getAllCartsList);

  const [cartCollections, setCartCollections] = useState([]);
  const { token } = useToken();
  const {
    collections,
    isFetching: isFetchingCollections,
    userId,
    userToken,
  } = useMarketplaceCartCollections();

  const [checkoutStates, setCheckoutStates] = useState({});
  const [activeProgressCollectionId, setActiveProgressCollectionId] =
    useState(null);

  const [isSwitching, setIsSwitching] = useState(false);

  useEffect(() => {
    setCartCollections(collections);
  }, [collections]);

  useEffect(() => {
    setCartCollections(cartsFromSlice);
  }, [cartsFromSlice]);

  const visibleCollections = useMemo(
    () =>
      (cartCollections || []).filter(
        (c) => (c?.paymentItem ?? []).length !== 0,
      ),
    [cartCollections],
  );

  const groupedByHost = useMemo(() => {
    const map = new Map();

    for (const collection of visibleCollections) {
      const host = collection?.host ?? {};
      const hostId = host.profileId ?? host.orgId ?? "unknown-host";

      if (!map.has(hostId)) {
        map.set(hostId, { host, hostId, collections: [] });
      }

      map.get(hostId).collections.push(collection);
    }

    return Array.from(map.values());
  }, [visibleCollections]);

  const { startSwitch, isSwitching: hookSwitching } = useSwitchingOrgs({
    token,
  });

  const { navigateTo } = useHistoryManager(storePath);

  const handleActivateBackButton = useCallback(
    (show, onClickBackButton = null) => {
      activateBackButton?.(show, onClickBackButton);
      setShowProgress(show);
    },
    [activateBackButton, setShowProgress],
  );

  const getCheckoutState = useCallback(
    (collectionId) => checkoutStates[collectionId] ?? "cartItems",
    [checkoutStates],
  );

  const setCheckoutStateFor = useCallback((collectionId, newState) => {
    setCheckoutStates((prev) => ({
      ...prev,
      [collectionId]: newState,
    }));
  }, []);

  useEffect(() => {
    setIsSwitching(hookSwitching);
  }, [hookSwitching]);

  const showSwitchingSkeleton =
    isSwitching || (isFetchingCollections && cartCollections.length === 0);

  return (
    <StoreBox {...props}>
      <div className={styles.cartContainer}>
        <div className={cx(styles.cartCase, styles.cartDivMarketplace)}>
          {groupedByHost.length > 0 && (
            <div className={styles.marketplaceCartHeading}>
              <p>
                You are currently shopping from{" "}
                {groupedByHost.length === 1
                  ? "one store"
                  : `${groupedByHost.length} stores`}
                , see below.
              </p>
            </div>
          )}

          {showSwitchingSkeleton ? (
            <MarketplaceSkeleton />
          ) : groupedByHost.length > 0 ? (
            groupedByHost.map((group) => {
              const groupKey = group.hostId;

              return (
                <div
                  key={groupKey}
                  style={{
                    padding: "30px",
                    background: "#fff",
                    borderRadius: "12px",
                    marginBottom: "24px",
                  }}
                >
                  <MarketplaceStoreDivider hostName={group.host?.name} />

                  <HostHeader
                    host={group.host}
                    onCheckout={() => startSwitch(group.collections[0])}
                  />

                  {group.collections.map((collection) => {
                    const collectionId = collection?.collectionId;
                    const itemsLength = collection?.paymentItem?.length ?? 0;
                    const key = `${collectionId}-${itemsLength}`;

                    return (
                      <div key={key}>
                        <CartModal
                          cart={collection}
                          showProduct={showProduct}
                          closeModal={closeModal}
                          accessPoint={accessPoint}
                          accessPoints={accessPoints}
                          activateBackButton={handleActivateBackButton}
                          checkoutState={getCheckoutState(collectionId)}
                          setCheckoutState={(st) =>
                            setCheckoutStateFor(collectionId, st)
                          }
                          publicUser={publicUser}
                          setPublicUser={setPublicUser}
                          isPageMode={isPageMode}
                          storePath={storePath}
                          navLocation={navLocation}
                          userId={userId}
                          userToken={userToken}
                          token={token}
                          activeProgressCollectionId={
                            activeProgressCollectionId
                          }
                          setActiveProgressCollectionId={
                            setActiveProgressCollectionId
                          }
                          startSwitch={startSwitch}
                          showMarketplaceCheckoutAction={false}
                          organisation={organisation}
                          cartSource="MARKETPLACE"
                        />
                      </div>
                    );
                  })}
                </div>
              );
            })
          ) : (
            <div className={styles.emptyCartMessage}>
              <span>{cartIcon}</span>
              <h3>Your cart is empty.</h3>
              <p>Looks like you haven't added anything to your cart yet.</p>
              <button
                className={styles.checkoutSecondaryButton}
                onClick={() => navigateTo("home", {}, false)}
              >
                Continue shopping
              </button>
            </div>
          )}
        </div>
      </div>
    </StoreBox>
  );
};

export default MarketplaceCartPage;
